import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFollowUps, updateFollowUp, deleteFollowUp, getUsers, getClients, getLeads } from '../../lib/api';
import { formatDate, getPriorityBadgeClass, getStatusBadgeClass, formatApiError, isOverdue } from '../../lib/utils';
import { FOLLOW_UP_TYPES, PRIORITIES, FOLLOWUP_TIME_SLOTS, buildUserOptions } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { TimePicker } from '../../components/ui/time-picker';
import { DatePicker } from '../../components/ui/date-picker';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, DotsThree, Check, Clock, Trash, CalendarCheck, Warning, Pencil } from '@phosphor-icons/react';

export default function FollowUpsPage() {
  const [followUps, setFollowUps] = useState([]);
  const [users, setUsers] = useState({});
  const [clients, setClients] = useState({});
  const [leads, setLeads] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [amFilter, setAmFilter] = useState('all');
  const [usersList, setUsersList] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [editingFu, setEditingFu] = useState(null);
  const [quickFilter, setQuickFilter] = useState('all'); // all | overdue | today | tomorrow | next3
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (typeFilter !== 'all') params.follow_up_type = typeFilter;
      if (amFilter !== 'all') params.assigned_to = amFilter;
      if (dateFrom) params.due_date_from = dateFrom;
      if (dateTo) params.due_date_to = dateTo;

      const [fuRes, usersRes, clientsRes, leadsRes] = await Promise.all([
        getFollowUps(params), getUsers(), getClients(), getLeads()
      ]);
      setFollowUps(fuRes.data);
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u.name; });
      setUsers(usersMap);
      setUsersList(usersRes.data);
      const clientsMap = {};
      clientsRes.data.forEach(c => { clientsMap[c.id] = c.full_name; });
      setClients(clientsMap);
      const leadsMap = {};
      (leadsRes.data || []).forEach(l => { leadsMap[l.id] = l.full_name || l.company_name || l.name || 'Lead'; });
      setLeads(leadsMap);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter, priorityFilter, typeFilter, amFilter, dateFrom, dateTo]);

  const handleComplete = async (id) => {
    try {
      await updateFollowUp(id, { status: 'Completed' });
      setFollowUps(followUps.map(f => f.id === id ? { ...f, status: 'Completed' } : f));
      toast.success('Follow-up marked as completed');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteFollowUp(deleteId);
      setFollowUps(followUps.filter(f => f.id !== deleteId));
      toast.success('Follow-up deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const handleEditSave = async () => {
    if (!editingFu) return;
    try {
      await updateFollowUp(editingFu.id, {
        due_date: editingFu.due_date,
        due_time: editingFu.due_time,
        follow_up_type: editingFu.follow_up_type,
        priority: editingFu.priority,
        description: editingFu.description,
      });
      setFollowUps(followUps.map(f => f.id === editingFu.id ? { ...f, ...editingFu } : f));
      toast.success('Follow-up updated');
      setEditingFu(null);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };



  const _today = new Date(); _today.setHours(0,0,0,0);
  const _tomorrow = new Date(_today); _tomorrow.setDate(_today.getDate() + 1);
  const _in3 = new Date(_today); _in3.setDate(_today.getDate() + 3);
  const todayStr = _today.toISOString().split('T')[0];
  const tomorrowStr = _tomorrow.toISOString().split('T')[0];

  const dueInDays = (d) => {
    if (!d) return Infinity;
    const dt = new Date(d); dt.setHours(0,0,0,0);
    return Math.floor((dt - _today) / 86400000);
  };

  const overdueCount = followUps.filter(f => f.status !== 'Completed' && isOverdue(f.due_date)).length;
  const todayCount = followUps.filter(f => f.status !== 'Completed' && f.due_date === todayStr).length;
  const tomorrowCount = followUps.filter(f => f.status !== 'Completed' && f.due_date === tomorrowStr).length;
  const next3Count = followUps.filter(f => f.status !== 'Completed' && dueInDays(f.due_date) >= 0 && dueInDays(f.due_date) <= 3).length;

  const visibleFollowUps = followUps.filter(f => {
    if (quickFilter === 'all') return true;
    if (quickFilter === 'overdue') return f.status !== 'Completed' && isOverdue(f.due_date);
    if (quickFilter === 'today') return f.status !== 'Completed' && f.due_date === todayStr;
    if (quickFilter === 'tomorrow') return f.status !== 'Completed' && f.due_date === tomorrowStr;
    if (quickFilter === 'next3') return f.status !== 'Completed' && dueInDays(f.due_date) >= 0 && dueInDays(f.due_date) <= 3;
    return true;
  });

  const FU_STATUSES = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

  return (
    <div className="p-6 space-y-6" data-testid="follow-ups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Follow-ups</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage scheduled client interactions</p>
        </div>
        <Button onClick={() => navigate('/follow-ups/new')} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="create-followup-button">
          <Plus size={18} className="mr-2" /> New Follow-up
        </Button>
      </div>

      {/* Quick Filter Cards — click to filter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: 'all', label: 'All', count: followUps.filter(f => f.status !== 'Completed').length, color: 'zinc', icon: CalendarCheck },
          { key: 'overdue', label: 'Overdue', count: overdueCount, color: 'red', icon: Warning },
          { key: 'today', label: 'Due Today', count: todayCount, color: 'amber', icon: Clock },
          { key: 'tomorrow', label: 'Tomorrow', count: tomorrowCount, color: 'blue', icon: CalendarCheck },
          { key: 'next3', label: 'Next 3 Days', count: next3Count, color: 'emerald', icon: CalendarCheck },
        ].map(chip => {
          const Icon = chip.icon;
          const active = quickFilter === chip.key;
          const ringMap = { red: 'ring-red-500', amber: 'ring-amber-500', blue: 'ring-blue-500', emerald: 'ring-emerald-500', zinc: 'ring-zinc-900' };
          const bgMap = { red: 'bg-red-50 text-red-600', amber: 'bg-amber-50 text-amber-600', blue: 'bg-blue-50 text-blue-600', emerald: 'bg-emerald-50 text-emerald-600', zinc: 'bg-zinc-100 text-zinc-600' };
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setQuickFilter(chip.key)}
              className={`rounded-sm border bg-white p-3 text-left transition-all hover:border-zinc-400 ${active ? `border-transparent ring-2 ${ringMap[chip.color]}` : 'border-zinc-200'}`}
              data-testid={`quick-filter-${chip.key}`}
            >
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-sm ${bgMap[chip.color]}`}><Icon size={14} weight="fill" /></div>
                <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-zinc-500">{chip.label}</span>
              </div>
              <p className={`text-2xl font-bold tabular-nums mt-1 ${chip.color === 'red' && chip.count > 0 ? 'text-red-600' : ''}`}>{chip.count}</p>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 filter-bar">
        <SearchableSelect
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[{ value: 'active', label: 'Active (excl. Completed)' }, { value: 'all', label: 'All Statuses' }, ...FU_STATUSES.map(s => ({ value: s, label: s }))]}
          placeholder="Status"
          triggerClassName="w-[200px]"
          data-testid="followup-status-filter"
        />
        <SearchableSelect
          value={priorityFilter}
          onValueChange={setPriorityFilter}
          options={[{ value: 'all', label: 'All Priorities' }, ...PRIORITIES.map(p => ({ value: p, label: p }))]}
          placeholder="Priority"
          triggerClassName="w-[140px]"
        />
        <SearchableSelect
          value={typeFilter}
          onValueChange={setTypeFilter}
          options={[{ value: 'all', label: 'All Types' }, ...FOLLOW_UP_TYPES.map(t => ({ value: t, label: t }))]}
          placeholder="Follow-up Type"
          triggerClassName="w-[200px]"
          data-testid="followup-type-filter"
        />
        <SearchableSelect
          value={amFilter}
          onValueChange={setAmFilter}
          options={[{ value: 'all', label: 'All Members' }, ...buildUserOptions(usersList.filter(u => u.status === 'active' || u.id))]}
          placeholder="Team (AM / SAM / OM)"
          searchPlaceholder="Search..."
          triggerClassName="w-[180px]"
          data-testid="followup-am-filter"
        />
        <div className="flex items-center gap-2">
          <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From" className="w-[150px]" data-testid="followup-date-from" />
          <span className="text-zinc-400 text-sm">to</span>
          <DatePicker value={dateTo} onChange={setDateTo} placeholder="To" className="w-[150px]" data-testid="followup-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs">Clear dates</Button>
        )}
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        {loading ? (
          <div className="p-6 space-y-4">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16" />)}</div>
        ) : visibleFollowUps.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarCheck size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No follow-ups found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {visibleFollowUps.map((fu) => {
              const isOverdueItem = fu.status !== 'Completed' && isOverdue(fu.due_date);
              return (
                <div key={fu.id} className={`p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-zinc-50 transition-colors ${isOverdueItem ? 'bg-red-50/50' : ''}`} data-testid={`followup-row-${fu.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-sm">{fu.follow_up_type}</p>
                      <Badge className={`${getPriorityBadgeClass(fu.priority)} text-xs`}>{fu.priority}</Badge>
                      <Badge className={`${getStatusBadgeClass(fu.status)} text-xs`}>{fu.status}</Badge>
                      {isOverdueItem && <Badge className="badge-overdue text-xs">Overdue</Badge>}
                    </div>
                    <p className="text-sm text-zinc-600 mt-1 truncate">
                      {fu.client_id && (clients[fu.client_id] || fu.client_name) ? (
                        <Link to={`/clients/${fu.client_id}`} className="hover:text-[#002FA7]">{clients[fu.client_id] || fu.client_name}</Link>
                      ) : fu.lead_id && (leads[fu.lead_id] || fu.lead_name) ? (
                        <Link to={`/leads/${fu.lead_id}`} className="hover:text-[#002FA7]">
                          {leads[fu.lead_id] || fu.lead_name} <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-1">Lead</span>
                        </Link>
                      ) : (
                        <span className="text-zinc-400">Contact unavailable</span>
                      )}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Due: {formatDate(fu.due_date)} {fu.due_time} · Assigned: {users[fu.assigned_to] || 'Unassigned'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-shrink-0 self-stretch sm:self-auto">
                    {fu.status !== 'Completed' && (
                      <Button variant="outline" size="sm" onClick={() => handleComplete(fu.id)} className="rounded-sm flex-1 sm:flex-initial">
                        <Check size={16} className="mr-1" /> Complete
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0"><DotsThree size={18} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingFu({ ...fu })} className="text-zinc-700">
                          <Pencil size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteId(fu.id)} className="text-red-600">
                          <Trash size={16} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Follow-up</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this follow-up?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Follow-up Dialog */}
      <Dialog open={!!editingFu} onOpenChange={() => setEditingFu(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Follow-up</DialogTitle>
          </DialogHeader>
          {editingFu && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Follow-up Type</Label>
                <SearchableSelect
                  value={editingFu.follow_up_type}
                  onValueChange={(v) => setEditingFu(p => ({ ...p, follow_up_type: v }))}
                  options={FOLLOW_UP_TYPES.map(t => ({ value: t, label: t }))}
                  placeholder="Select type"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Due Date</Label>
                  <DatePicker
                    value={editingFu.due_date}
                    onChange={(v) => setEditingFu(p => ({ ...p, due_date: v }))}
                    placeholder="Select date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Due Time</Label>
                  <TimePicker
                    value={editingFu.due_time || ''}
                    onChange={(v) => setEditingFu(p => ({ ...p, due_time: v }))}
                    slots={FOLLOWUP_TIME_SLOTS}
                    placeholder="Select time"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Priority</Label>
                <SearchableSelect
                  value={editingFu.priority}
                  onValueChange={(v) => setEditingFu(p => ({ ...p, priority: v }))}
                  options={PRIORITIES.map(p => ({ value: p, label: p }))}
                  placeholder="Select priority"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Description</Label>
                <Input
                  value={editingFu.description || ''}
                  onChange={(e) => setEditingFu(p => ({ ...p, description: e.target.value }))}
                  placeholder="Description"
                  className="rounded-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFu(null)}>Cancel</Button>
            <Button onClick={handleEditSave} className="bg-[#002FA7] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
